<script lang="ts">
  import { onMount } from 'svelte';
  import SendMessage from '$icons/SendMessageIcon.svelte';
  import type { FPost } from 'garden-common';
  import type { IComment, WithCreatorData } from 'garden-common';
  import { getPostComments } from '$src/utils/api/comments.util';

  import dayjs from 'dayjs';
  import TagPopUp from '$lib/components/common/TagPopUp.svelte';
  import relativeTime from 'dayjs/plugin/relativeTime';
  import LoaderRows from '$lib/components/common/loaders/LoaderRows.svelte';
  import type { IUser } from 'garden-common';
  import CollapseSection from '$lib/components/common/CollapseSection.svelte';
  import CommentItem from './CommentItem.svelte';
  import DonateModal from '$lib/components/CreatePost/DonateModal.svelte';
  import Loader from '$icons/LoaderIcon.svelte';
  import { me } from '$stores/user.store';
  import EmojiHappy from '$icons/EmojiHappyIcon.svelte';
  import type { PostsCore } from '$src/utils/postsCore.util';
  import { writeTextAtCursor, moveCursorToEnd } from '$utils/inputs.util';
  import { tick } from 'svelte';

  dayjs.extend(relativeTime);

  export let post: Partial<FPost<string>> & Partial<WithCreatorData>;
  export let core: PostsCore<IUser<string>>;

  let comments: IComment<string>[] = [];
  let commenters: IUser<string>[] = [];
  let commentterList: string[] = [];

  let commentInput: HTMLElement;
  let commentText = '';

  $: if (commentText?.length > 500) {
    commentText = commentText.slice(0, 500);
  }

  let modalState: boolean = false;
  let parentComment: IComment<string> | null = null;
  let inputSelected: boolean = false;
  let tagModalOpen: boolean = false;
  let selectedCommentter: any;
  let taggedUserName = '';
  let tagDetect = false;
  let taggedUsers: string[] = [];
  let loading = true;
  let emojiPickerOpen = false;
  let submitLoading = false;

  async function handleDonation(e?: CustomEvent<string>) {
    let newComment;
    submitLoading = true;

    const amount = e?.detail ? parseInt(e.detail, 10) : 0;

    // get tagged users
    const usersInCommentText = commentText
      .split(' ')
      .filter(word => word.startsWith('@'))
      .map(word => word.slice(1));

    taggedUsers = taggedUsers.filter(user => usersInCommentText.includes(user));

    if (parentComment) {
      newComment = await core.CommentReply({
        comment: { commentText: commentText, taggedUser: taggedUsers },
        parentComment: parentComment._id,
        donatedSeeds: amount
      });
    } else {
      newComment = await core.Comment({
        comment: { commentText: commentText, taggedUser: taggedUsers },
        donatedSeeds: amount
      });
    }

    if (!newComment) return;

    comments = [...comments, newComment];

    parentComment = null;
    commentText = '';

    submitLoading = false;
    setTimeout(updateComments, 2000); //Issues with BE
  }

  const updateComments = async () => {
    if (post.comments.length > 0) {
      comments = await getPostComments(post._id);
      getCommenters(comments);
    }
    loading = false;
  };

  const selecteCommentter = async (taggedUser: typeof selectedCommentter) => {
    if (commentText.length + taggedUserName.length > 500) {
      // TODO: Show error message
      return;
    }

    await writeTextAtCursor(`${taggedUser.userName} `);
    await moveCursorToEnd(commentInput);

    await tick();

    commentText = commentInput.textContent;

    if (!taggedUsers.includes(taggedUser.userName)) taggedUsers.push(taggedUser.userName);
    tagModalOpen = false;
    commentInput.focus(); //focus back on input
  };

  const onKeyPressedHandler = (e: KeyboardEvent) => {
    if (comments.length === 0) return;

    if (
      commentText.length >= 500 &&
      e.key !== 'Backspace' &&
      e.key !== 'Delete' &&
      e.key !== 'ArrowLeft' &&
      e.key !== 'ArrowRight' &&
      e.key !== 'ArrowUp' &&
      e.key !== 'ArrowDown'
    ) {
      console.log('max length reached');
      e.preventDefault();
      return;
    }

    if (e.key === 'Backspace') {
      tagModalOpen = false;
    } else if (e.key === '@' && tagModalOpen === false) {
      tagModalOpen = !tagModalOpen;
    }
    if (tagModalOpen) {
      if (tagModalOpen && (e.key === 'Enter' || e.key === 'Tab')) {
        selecteCommentter(selectedCommentter);
      }
    }
  };

  const getCommenters = (comments: any[]) => {
    for (let i = 0; i < comments.length; i++) {
      const c = comments[i];
      if (!commentterList.includes(c.commentPoster?._id) && c.commentPoster?._id) {
        commenters.push(c.commentPoster);
        commentterList.push(c.commentPoster?._id);
      }
    }

    if (commenters.length > 0) {
      selectedCommentter = commenters[0];
    }
  };

  const handlePostCommentClick = (): void => {
    if (commentText?.length > 500) return;
    if ($me._id === post.creator || commenters.findIndex(c => c._id === $me._id) >= 0) {
      handleDonation();
      return;
    }

    if (commentText?.length > 0) modalState = true;
    return;
  };

  $: if (comments.length > 0) getCommenters(comments);

  const setParentComment = (comment: IComment<string>) => {
    parentComment = comment;
    commentInput.focus();
  };

  const handleWindowClick = e => {
    if (!e.target.closest('#uep')) {
      emojiPickerOpen = false;
    }
  };

  onMount(async () => {
    await updateComments();
  });
</script>

<svelte:window on:click={handleWindowClick} />

<!-- Replace with a proper comments modal. Leaving what was here as it was for now -->
{#if modalState}
  <DonateModal
    on:Donate={handleDonation}
    {post}
    on:ModalClose={() => (modalState = false)}
    minimumSeed={10}
  />
{/if}

<div class="relative h-full flex flex-col">
  <CollapseSection contentOpen>
    <div slot="title" class="font-medium text-blueish-grey-900">
      {comments.length || 0} Comments
    </div>
    <!-- max-h-[calc(100vh-640px)] -->
    <div
      class="border-t border-grey-transparency-dark border-opacity-5 pt-4 mt-4 flex flex-col gap-8"
    >
      <div class="flex mt-2 z-[30] flex-col gap-3 overflow-scroll scrollbar-hide">
        {#key comments}
          {#if loading}
            <LoaderRows rows={1} />
          {:else if comments.length > 0}
            {#each comments as c, i}
              {#if !c.parentComment}
                <CommentItem
                  {c}
                  {i}
                  me={$me}
                  {comments}
                  on:reply={e => setParentComment(e.detail)}
                />
              {/if}
            {/each}
          {:else}
            <p class="text-sm text-center text-[#aab8c8e8]">
              No comments on this post. Why not send them one!
            </p>
          {/if}
        {/key}
      </div>
    </div>
  </CollapseSection>

  <div class="flex flex-col z-[50] sticky -bottom-10 py-6 w-full bg-white">
    {#if parentComment}
      <div class="flex items-center text-blueish-grey-600 justify-between pb-2">
        <div class="pr-4 whitespace-nowrap max-w-full overflow-hidden text-ellipsis">
          Replying to <span class="text-color-green-primary">
            @{parentComment.commentPoster.userName}
          </span>: {parentComment.commentText}
        </div>
        <button
          on:click={() => {
            parentComment = null;
          }}
          class="text-color-red-base hover:text-color-red-light"
        >
          Cancel
        </button>
      </div>
    {/if}

    {#if emojiPickerOpen}
      <div class="z-[1001] absolute items-start bottom-12">
        <unicode-emoji-picker
          class="w-[5vw] h-[5vw]"
          id="uep"
          on:emoji-pick={e => {
            commentText += e.detail.emoji;
            // emojiPickerOpen = false;
          }}
        />
      </div>
    {/if}

    <div class=" w-full flex items-center justify-center flex-grow gap-3 h-full">
      <button
        class="flex-none cursor-pointer"
        id="uep"
        on:click={() => {
          emojiPickerOpen = !emojiPickerOpen;
        }}
      >
        <EmojiHappy />
      </button>
      {#if tagModalOpen}
        {#if commenters.length > 0}
          <div
            class="flex flex-col absolute border bottom-[5rem] z-[10000] w-full bg-white max-h-[200px] rounded-xl overflow-y-scroll"
          >
            {#each commenters as commentter}
              <button
                class="w-full cursor-pointer flex group {selectedCommentter?.userName ===
                commentter.userName
                  ? 'bg-slate-200 hover:bg-slate-100'
                  : 'hover:bg-slate-200 bg-white'}"
                on:click={() => selecteCommentter(commentter)}
              >
                <TagPopUp {commentter} />
              </button>
            {/each}
          </div>
        {/if}
      {/if}

      <div
        class="w-full relative cursor-text"
        class:pointer-events-none={!!!post?.creatorData._id}
        class:opacity-75={!!!post?.creatorData._id}
      >
        {#if !inputSelected && commentText.length == 0}
          <span class="pointer-events-none absolute left-4 top-3 opacity-50 select-none"
            >Type message here</span
          >
        {/if}

        <div
          role="dialog"
          class="whitespace-pre-line w-full flex-col items-center flex-grow-0 break-all px-5 py-3 rounded-[14px] border focus:border-color-green-primary outline-none"
          bind:this={commentInput}
          contenteditable="true"
          bind:textContent={commentText}
          on:keydown={onKeyPressedHandler}
          on:focus={() => {
            inputSelected = true;
          }}
          on:blur={() => {
            inputSelected = false;
          }}
        />
      </div>

      <button
        disabled={!post?.creatorData._id}
        class:pointer-events-none={!post?.creatorData._id}
        class:opacity-75={!!!post?.creatorData._id}
        on:click={handlePostCommentClick}
        class="flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-[14px] cursor-pointer bg-brand-green-50"
      >
        {#if submitLoading}
          <Loader size={24} />
        {:else}
          <SendMessage />
        {/if}
      </button>
    </div>
  </div>
</div>
